import type { MouseEvent, PointerEvent, ReactNode } from "react";

import { clsx } from "clsx";

import { RVIconSizeOptions } from "../../atoms/RVIcon/RVIcon";
import { RVRow } from "../../layouts/RVFlexbox/RVFlexbox";
import { RVButton } from "../RVButton/RVButton";

import styles from "./RVNavigatorControl.module.scss";

export type RVNavigatorControlProps = {
    /** Content shown between previous/next controls, such as "2 of 8". */
    label: ReactNode;
    /** Called when the previous control is activated. */
    onPrev: () => void;
    /** Called when the next control is activated. */
    onNext: () => void;
    /** Disables the previous control when true. */
    prevDisabled?: boolean;
    /** Disables the next control when true. */
    nextDisabled?: boolean;
    /** Accessible label for the previous control. Defaults to "Previous". */
    previousAriaLabel?: string;
    /** Accessible label for the next control. Defaults to "Next". */
    nextAriaLabel?: string;
    /** Optional class for consumer-level positioning/layout. */
    className?: string;
    /** Optional test id applied to the root container. */
    testId?: string;
    /** Stops pointer/click propagation to parent containers when true. */
    preventParentPointerEvents?: boolean;
};

function stopPropagation(event: MouseEvent | PointerEvent): void {
    event.stopPropagation();
}

/**
 * Compact previous/next control used to navigate between indexed changes.
 */
export function RVNavigatorControl({
    label,
    onPrev,
    onNext,
    prevDisabled = false,
    nextDisabled = false,
    previousAriaLabel = "Previous",
    nextAriaLabel = "Next",
    className,
    testId,
    preventParentPointerEvents = false,
}: RVNavigatorControlProps): JSX.Element {
    return (
        <div
            className={clsx(styles.control, className)}
            data-testid={testId}
            onClick={preventParentPointerEvents ? stopPropagation : undefined}
            onMouseDown={preventParentPointerEvents ? stopPropagation : undefined}
            onPointerDown={preventParentPointerEvents ? stopPropagation : undefined}
        >
            <RVRow alignItems="center" gap={1}>
                <RVButton
                    type="text"
                    textVariant="icon"
                    icon="arrow-up"
                    iconSize={RVIconSizeOptions.XSMALL}
                    className={styles.button}
                    aria-label={previousAriaLabel}
                    disabled={prevDisabled}
                    onClick={(event) => {
                        if (preventParentPointerEvents) {
                            event.stopPropagation();
                        }
                        onPrev();
                    }}
                />
                <span className={styles.label} translate="no">
                    {label}
                </span>
                <RVButton
                    type="text"
                    textVariant="icon"
                    icon="arrow-down"
                    iconSize={RVIconSizeOptions.XSMALL}
                    className={styles.button}
                    aria-label={nextAriaLabel}
                    disabled={nextDisabled}
                    onClick={(event) => {
                        if (preventParentPointerEvents) {
                            event.stopPropagation();
                        }
                        onNext();
                    }}
                />
            </RVRow>
        </div>
    );
}
